<template lang="pug">
study-result-list.study-result-management(
  ref='template',
  :userRole='userRole',
  :currentLink='currentLink',
  :sideMenuLinkEvent='sideMenuLinkEvent',
  :logoutEvent='logoutEvent',
  :breadcrumbs='breadcrumbs',
  :yearSelectOptions='yearSelectOptions',
  :monthSelectOptions='monthSelectOptions',
  :searchEvent='searchEvent',
  :studyLists='studyLists',
  :firstButtonEvent='firstButtonEvent',
  :secondButtonEvent='secondButtonEvent',
  :thirdButtonEvent='thirdButtonEvent',
  @yearUpdated='setMonthOptions'
)
  template(#sideMenuSeparateText) 設定
  template(#logoutText) サインアウト
  template(#tabLabel) CBT
  template(#searchButtonText) 絞り込む
  template(#titleHeader) テスト名
  template(#subheadYearLabel) 実施年度：
  template(#subheadMonthLabel) 実施月：
  template(#titleLabel) 学年：
  template(#firstButtonLabel) 成績表一括ダウンロード
  template(#secondButtonLabel) 非表示
  template(#thirdButtonLabel) 一覧データダウンロード
</template>

<script lang="ts">
import { Component, Mixins, Ref } from 'vue-property-decorator'
import LoginMethods from '@/mixins/LoginMethods'
import StudyContentsApi from '@/mixins/api/StudyContentsApi'
import OriginalCurriculumsApi from '@/mixins/api/OriginalCurriculumsApi'
import StudyResultDetailsApi from '@/mixins/api/StudyResultDetailsApi'
import StudyResultList from '@/components/templates/manager/history/StudyResultList.vue'
import { breadcrumbType } from '@/models/breadcrumbModel'
import { StudyListType, StudyManagementItem, StudySearchParamsType } from '@/models/studyListModel'
import { InputItem } from '@/models/inputItem'
import { LinkSetting, SideMenuSettings } from '@/const/SideMenuSettings'
import { UserRoles } from '@/const/UserRoles'
import { InputItemPlaceholderSetting } from '@/const/DisplaySettings'
import { StudySearchUtils } from '@/utils/StudySearchUtils'
import { StudyManagementUtils } from '@/utils/StudyManagementUtils'
import { StudyResultUtils } from '@/utils/StudyResultUtils'

/**
 * 履歴管理・テスト一覧（塾管理者）
 */
@Component({
  components: {
    StudyResultList,
  },
})
export default class StudyResultManagement extends Mixins(
  LoginMethods,
  StudyContentsApi,
  OriginalCurriculumsApi,
  StudyResultDetailsApi
) {
  // テンプレートへのアクセス
  @Ref() template!: StudyResultList

  // パンくず
  private breadcrumbs: breadcrumbType[] = [
    {
      label: '履歴管理',
    },
  ]

  // 塾管理者権限
  private userRole: UserRoles = UserRoles.ACADEMY

  // サイドメニューの現在リンク
  private currentLink: LinkSetting = SideMenuSettings.MANAGER_HISTORY

  // 実施年度選択プルダウンのプレースホルダー
  private yearSelectPlaceholder: InputItem = StudySearchUtils.buildPlaceholder(InputItemPlaceholderSetting.YEAR_REQUIED)

  // 実施月選択プルダウンのプレースホルダー
  private monthSelectPlaceholder: InputItem = StudySearchUtils.buildPlaceholder(
    InputItemPlaceholderSetting.MONTH_REQUIED
  )

  // 実施年度選択オプション
  private yearSelectOptions: InputItem[] = StudySearchUtils.buildYearInputItems(this.yearSelectPlaceholder)

  // 実施月選択オプション
  private monthSelectOptions: InputItem[] = StudySearchUtils.buildMonthInputItems(this.monthSelectPlaceholder)

  // テスト一覧表示情報
  private studyLists: StudyListType[] = []

  // 初期表示自動検索実行中フラグ
  private isProcessingAutomaticSearch = false

  // 塾管理画面Cookie情報
  private get academyCookie() {
    return this.$bcbtCookies.academyCookie.get() || {}
  }

  // 初期表示処理
  private async mounted() {
    this.$loading.start()
    // 実施年度選択オプションを取得
    const apiResponse = await this.getYears()
    this.yearSelectOptions = StudySearchUtils.buildYearInputItems(this.yearSelectPlaceholder, apiResponse)
    // Cookieに検索条件がセットされている場合は設定して自動検索
    const studyResultManagementSearch = this.academyCookie.studyResultManagementSearch
    if (studyResultManagementSearch) {
      this.isProcessingAutomaticSearch = true
      this.template.yearValue = String(studyResultManagementSearch.years)
      this.template.monthValue = String(studyResultManagementSearch.month)
      await this.search()
    }
    this.$loading.complete()
  }

  // 実施月オプションのセット処理（実施年度変更時）
  private async setMonthOptions() {
    this.$loading.start()
    // 実施年度未選択状態の場合、プルダウンは空になる
    const apiResponse = this.template.yearValue ? await this.getMonths(Number(this.template.yearValue)) : null
    this.monthSelectOptions = StudySearchUtils.buildMonthInputItems(this.monthSelectPlaceholder, apiResponse)
    // 初期表示自動検索時以外は実施月を未選択状態に戻す
    if (!this.isProcessingAutomaticSearch) {
      this.template.monthValue = ''
    }
    this.isProcessingAutomaticSearch = false
    this.$loading.complete()
  }

  // 検索ボタン押下イベント
  private async searchEvent() {
    // 検索
    await this.search()
  }

  // 検索処理
  private async search() {
    this.$loading.start()
    const params: StudySearchParamsType = {
      years: Number(this.template.yearValue),
      month: Number(this.template.monthValue),
    }
    // API呼び出し
    const apiResponse = await this.searchStudyContents(params, this.userRole)
    this.studyLists = StudyManagementUtils.formatStudyLists(apiResponse)
    // Cookieに検索条件をセット
    const academyCookie = this.academyCookie
    academyCookie.studyResultManagementSearch = params
    await this.$bcbtCookies.academyCookie.set(academyCookie)
    this.$loading.complete()
  }

  // サイドメニュー遷移イベント
  private sideMenuLinkEvent(link: LinkSetting) {
    // 現ページのリンク押下時は遷移処理なし
    if (link === this.currentLink) return

    // 設定されたリンク先ページへ遷移
    this.$router.push({ name: `Academy${link.name}` })
  }

  // ログアウトイベント
  private logoutEvent() {
    this.logout()
    // ログインページへ遷移
    this.$router.push({ name: 'AcademyLogin' })
  }

  // 成績表一括ダウンロードイベント（1つ目のボタン）
  private async firstButtonEvent(item: StudyManagementItem) {
    // Cookieから実施年度選択情報を取得しリクエストパラメータに使用
    if (!this.academyCookie.studyResultManagementSearch) return

    // ダウンロード処理
    this.$loading.start()
    try {
      // zipデータとヘッダー情報を取得
      const [data, headers] = await this.downloadStudyResultPdfStudents(
        Number(this.academyCookie.studyResultManagementSearch.years),
        item.originalCurriculumId,
        this.userRole
      )
      // aタグを生成し、hrefにデータを設定
      const fileLink = document.createElement('a')
      fileLink.href = URL.createObjectURL(data)
      // ヘッダー情報からファイル名を取得して設定
      fileLink.download = StudyResultUtils.getFilename(headers) || 'file_name_error.zip'
      // clickを実行することでファイルをダウンロード
      fileLink.click()
      this.$loading.complete()
    } catch {
      alert('ファイルが存在しません。')
    }
  }

  // 帳票ダウンロードイベント（2つ目のボタン、現在非表示）
  private async secondButtonEvent(item: StudyManagementItem) {
    // Cookieから実施年度選択情報を取得しリクエストパラメータに使用
    if (!this.academyCookie.studyResultManagementSearch) return

    // ダウンロード処理
    this.$loading.start()
    try {
      // zipデータとヘッダー情報を取得
      const [data, headers] = await this.downloadStudyResultPdf(
        Number(this.academyCookie.studyResultManagementSearch.years),
        item.originalCurriculumId,
        this.userRole
      )
      // aタグを生成し、hrefにデータを設定
      const fileLink = document.createElement('a')
      fileLink.href = URL.createObjectURL(data)
      // ヘッダー情報からファイル名を取得して設定
      fileLink.download = StudyResultUtils.getFilename(headers) || 'file_name_error.zip'
      // clickを実行することでファイルをダウンロード
      fileLink.click()
      this.$loading.complete()
    } catch {
      alert('ファイルが存在しません。')
    }
  }

  // CSVダウンロードイベント（3つ目のボタン）
  private async thirdButtonEvent(item: StudyManagementItem) {
    // Cookieから実施年度選択情報を取得しリクエストパラメータに使用
    if (!this.academyCookie.studyResultManagementSearch) return

    // ダウンロード処理
    this.$loading.start()
    try {
      // zipデータとヘッダー情報を取得
      const [data, headers] = await this.downloadStudyResultExcel(
        Number(this.academyCookie.studyResultManagementSearch.years),
        item.originalCurriculumId,
        this.userRole
      )
      // aタグを生成し、hrefにデータを設定
      const fileLink = document.createElement('a')
      fileLink.href = URL.createObjectURL(data)
      // ヘッダー情報からファイル名を取得して設定
      fileLink.download = StudyResultUtils.getFilename(headers) || 'file_name_error.zip'
      // clickを実行することでファイルをダウンロード
      fileLink.click()
      this.$loading.complete()
    } catch {
      alert('ファイルが存在しません。')
    }
  }
}
</script>
